import React, { useEffect, useState } from 'react'
import { useDataProvider } from 'react-admin'
import { Link } from 'react-router-dom'
import {
    Box,
    Chip,
    CircularProgress,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core'

const ACTIONS = {
    CREATE: { label: 'Criação', color: '#2e7d32' },
    UPDATE: { label: 'Atualização', color: '#1565c0' },
    DELETE: { label: 'Exclusão', color: '#c62828' },
}

const formatDateTime = (value) =>
    value ? new Date(value).toLocaleString('pt-BR') : '-'

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '-'
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
}

const UpdatedFields = ({ fields }) => {
    if (!fields || !Object.keys(fields).length) return '-'

    return Object.entries(fields).map(([field, change]) => (
        <div key={field}>
            <strong>{field}</strong>: {formatValue(change?.before)} →{' '}
            {formatValue(change?.now)}
        </div>
    ))
}

export const AssistanceCouponLogs = ({ couponId }) => {
    const dataProvider = useDataProvider()
    const [logs, setLogs] = useState([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [page, setPage] = useState(0)
    const [perPage, setPerPage] = useState(10)
    const [action, setAction] = useState('')
    const [user, setUser] = useState('')
    const [userFilter, setUserFilter] = useState('')

    useEffect(() => {
        const timeout = setTimeout(() => {
            setUserFilter(user.trim())
            setPage(0)
        }, 500)
        return () => clearTimeout(timeout)
    }, [user])

    useEffect(() => {
        let active = true
        setLoading(true)
        setError(null)

        const filter = {}
        if (action) filter.action = action
        if (userFilter) filter.user = userFilter

        const resource = couponId
            ? `admin/assistanceCoupons/${couponId}/logs`
            : 'admin/assistanceCoupons/logs'

        dataProvider
            .getList(resource, {
                pagination: { page: page + 1, perPage },
                sort: { field: 'createdAt', order: 'DESC' },
                filter,
            })
            .then(({ data, total }) => {
                if (!active) return
                setLogs(data)
                setTotal(total)
            })
            .catch((err) => {
                if (!active) return
                setLogs([])
                setTotal(0)
                setError(
                    err?.body?.message ||
                        err?.message ||
                        'Erro ao consultar os logs do cupom.'
                )
            })
            .finally(() => active && setLoading(false))

        return () => {
            active = false
        }
    }, [dataProvider, couponId, page, perPage, action, userFilter])

    return (
        <Paper>
            <Box display="flex" style={{ gap: 16 }} p={2}>
                <TextField
                    select
                    label="Ação"
                    variant="filled"
                    size="small"
                    value={action}
                    onChange={(e) => {
                        setAction(e.target.value)
                        setPage(0)
                    }}
                    style={{ minWidth: 180 }}
                >
                    <MenuItem value="">Todas</MenuItem>
                    {Object.entries(ACTIONS).map(([id, { label }]) => (
                        <MenuItem key={id} value={id}>
                            {label}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Usuário"
                    variant="filled"
                    size="small"
                    value={user}
                    onChange={(e) => setUser(e.target.value)}
                />
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box p={2}>
                    <Typography color="error">{error}</Typography>
                </Box>
            ) : !logs.length ? (
                <Box p={2}>
                    <Typography>
                        {couponId
                            ? 'Nenhum log encontrado para este cupom.'
                            : 'Nenhum log encontrado.'}
                    </Typography>
                </Box>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Data</TableCell>
                            {!couponId && <TableCell>Cupom</TableCell>}
                            <TableCell>Usuário</TableCell>
                            <TableCell>Ação</TableCell>
                            <TableCell>Detalhes</TableCell>
                            <TableCell>Campos alterados</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {logs.map((log) => (
                            <TableRow key={log.id}>
                                <TableCell>
                                    {formatDateTime(log.createdAt)}
                                </TableCell>
                                {!couponId && (
                                    <TableCell>
                                        {log.couponId ? (
                                            <Link
                                                to={`/admin/assistanceCoupons/${log.couponId}`}
                                            >
                                                #{log.couponId}
                                            </Link>
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>
                                )}
                                <TableCell>{log.user || '-'}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={
                                            ACTIONS[log.action]?.label ||
                                            log.action
                                        }
                                        style={{
                                            color: '#fff',
                                            backgroundColor:
                                                ACTIONS[log.action]?.color,
                                        }}
                                    />
                                </TableCell>
                                <TableCell>{log.details || '-'}</TableCell>
                                <TableCell>
                                    <UpdatedFields fields={log.updatedFields} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <TablePagination
                component="div"
                count={total}
                page={page}
                rowsPerPage={perPage}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="Linhas por página"
                labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count}`
                }
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                    setPerPage(parseInt(e.target.value, 10))
                    setPage(0)
                }}
            />
        </Paper>
    )
}
